import { useState, useEffect } from "react";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { supabase } from '../lib/supabase';
import { showToast } from '../utils/toast';
import { friendlyError } from '../lib/utils';

const C = { cream:'#fdf8f0', cream2:'#f5f0e4', blue:'#1562c8', gold:'#f0bf6b', navy:'#01306d', brown:'#55381f', dark:'#2a2118', white:'#ffffff', green:'#1a8a4a', red:'#c0392b' };
const BORDER = `2px solid ${C.dark}`;
const SHADOW = `6px 6px 0 ${C.dark}`;
const SHADOW_SM = `3px 3px 0 ${C.navy}`;

const GENRE_LIST = [
  'Classical', 'Jazz', 'Electronic', 'Hip-Hop', 'Pop',
  'Film Score', 'Ambient', 'R&B', 'Afrobeats', 'World Music'
];

export function AuthPage({ onAuthComplete, onBackToLanding, onGoogleSignIn, onApplyAgency, initialError = "" }) {
  const [authView, setAuthView] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [signupStep, setSignupStep] = useState(1);   // 1 = credentials, 2 = profile info

  // Step 1 fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 1+2 fields
  const [role, setRole] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [genres, setGenres] = useState([]);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 480);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const switchView = (view) => {
    setAuthView(view);
    setSignupStep(1);
    setError("");
    setShowPassword(false);
    setConfirmPassword("");
    setShowConfirmPassword(false);
    setRole("");
    setAgreedToTerms(false);
  };

  // Password strength checker — logic unchanged
  const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: C.red };
    if (score === 2) return { level: 2, label: 'Fair', color: '#f59e0b' };
    if (score === 3) return { level: 3, label: 'Good', color: C.blue };
    return { level: 4, label: 'Strong', color: C.green };
  };

  const passwordStrength = getPasswordStrength(password);

  // Step 1 → Step 2 — logic unchanged
  const handleNextStep = (e) => {
    e.preventDefault();
    setError("");
    if (!role) {
      setError("Please select your role to continue.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must include at least one uppercase letter.");
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError("Password must include at least one number.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match. Please check and try again.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setSignupStep(2);
  };

  // Final submit — logic unchanged
  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (authView === 'signup') {
        if (!firstName.trim() || !lastName.trim()) { setError("First and last name are required."); setLoading(false); return; }

        // 1. Create auth account
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + '/?welcome=1' },
        });
        if (signUpError) throw signUpError;

        // 2. If email confirmation is required, no session/JWT exists yet — skip profile insert
        //    (RLS would block it). Store profile data so App.jsx can auto-create it on first login.
        if (!data.session) {
          localStorage.setItem('sp_pending_signup_email', email.toLowerCase().trim());
          localStorage.setItem('sp_pending_profile', JSON.stringify({
            role,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            bio: bio.trim(),
            location: location.trim(),
            genres,
            marketingConsent,
            marketingConsentAt: marketingConsent ? new Date().toISOString() : null,
          }));
          setAuthView('verify_email');
          setLoading(false);
          return;
        }

        // 3. Session available — create profile record immediately
        const accountType = role === 'executive' ? 'music_executive' : role === 'organization' ? 'organization' : 'composer';
        const profileRow = {
          user_id: data.user.id,
          role,
          account_type: accountType,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          bio: bio.trim() || null,
          location: location.trim() || null,
          avatar_color: `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`,
          marketing_consent: marketingConsent,
          marketing_consent_at: marketingConsent ? new Date().toISOString() : null,
        };

        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .insert([profileRow])
          .select('*, composers(*)')
          .single();
        if (profileError) throw profileError;

        // 4. Composer genres (optional)
        if (role === 'composer' && genres.length > 0) {
          await supabase.from('composers').insert([{
            user_profile_id: profileData.id,
            genres,
          }]);
        }

        showToast.success('Account created! Welcome to Coda-Vault.');
        onAuthComplete(data.user, profileData);

      } else if (authView === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        showToast.success("Password reset link sent! Check your email inbox.");
        switchView('login');

      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuthComplete(data.user);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputStyle = {
    width: "100%",
    background: C.cream,
    border: BORDER,
    borderRadius: 8,
    padding: "10px 14px",
    color: C.dark,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Roboto',sans-serif",
    transition: "box-shadow 0.1s",
  };

  const labelStyle = {
    color: C.dark,
    fontSize: 12,
    fontWeight: 700,
    display: 'block',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  };

  const focusInput = (e) => {
    e.currentTarget.style.boxShadow = `2px 2px 0 ${C.dark}`;
  };
  const blurInput = (e) => {
    e.currentTarget.style.boxShadow = "none";
  };

  const primaryBtnStyle = {
    width: "100%",
    background: C.blue,
    color: C.white,
    border: BORDER,
    borderRadius: 8,
    padding: "13px",
    fontWeight: 700,
    fontSize: 14,
    cursor: loading ? "not-allowed" : "pointer",
    fontFamily: "'GeistPixel',monospace",
    opacity: loading ? 0.6 : 1,
    boxShadow: SHADOW_SM,
    marginBottom: 12,
    transition: "box-shadow 0.1s, transform 0.1s",
  };

  const pageStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px 40px",
    background: C.cream,
    fontFamily: "'Roboto',sans-serif",
    position: 'relative',
  };

  const cardStyle = (wider = false) => ({
    background: C.gold,
    border: BORDER,
    boxShadow: SHADOW,
    borderRadius: 12,
    padding: isMobile ? '24px 20px' : '36px 40px',
    width: '100%',
    maxWidth: wider ? 520 : 440,
    margin: '20px 0',
  });

  // Scanline texture overlay (matches mockup)
  const Scanlines = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(42,33,24,0.012) 3px,rgba(42,33,24,0.012) 4px)', pointerEvents: 'none', zIndex: 999 }} />
  );

  // ── Step indicator: numbered circles connected by a line ──────────────────
  const StepBar = ({ step }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
      {[1, 2].map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
          {i > 0 && (
            <div style={{ width: 28, height: 2, background: step > 1 ? C.green : 'rgba(42,33,24,0.15)' }} />
          )}
          <div style={{
            width: 26, height: 26, borderRadius: '50%', border: BORDER,
            background: step > s ? C.green : step === s ? C.dark : 'rgba(42,33,24,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800,
            color: step >= s ? C.white : C.brown,
            flexShrink: 0,
          }}>
            {step > s ? '✓' : s}
          </div>
        </div>
      ))}
    </div>
  );

  // Inline error block
  const ErrorMsg = ({ msg }) => msg ? (
    <div style={{ color: C.red, fontSize: 13, marginBottom: 14, textAlign: "center", padding: 12, background: 'rgba(192,57,43,0.08)', borderRadius: 8, border: '1px solid rgba(192,57,43,0.3)' }} role="alert">
      {msg}
    </div>
  ) : null;

  // ── VERIFY EMAIL SCREEN ───────────────────────────────────────────────────
  if (authView === 'verify_email') {
    const handleResend = async () => {
      setLoading(true);
      try {
        const { error } = await supabase.auth.resend({ type: 'signup', email });
        if (error) throw error;
        showToast.success('Verification email resent!');
      } catch (err) {
        showToast.error(friendlyError(err));
      } finally {
        setLoading(false);
      }
    };

    return (
      <div style={pageStyle}>
        <Scanlines />
        <div style={{ ...cardStyle(), textAlign: 'center' }}>
          {/* 3-step indicator: step 1 done, step 2 active, step 3 pending */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
            {[
              { n: '✓', bg: C.green },
              { n: '2', bg: C.dark },
              { n: '3', bg: 'rgba(42,33,24,0.08)' },
            ].map((dot, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                {i > 0 && <div style={{ width: 28, height: 2, background: i === 1 ? C.green : 'rgba(42,33,24,0.15)' }} />}
                <div style={{ width: 26, height: 26, borderRadius: '50%', border: BORDER, background: dot.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: i < 2 ? C.white : C.brown, flexShrink: 0 }}>
                  {dot.n}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 48, marginBottom: 12 }}>📬</div>
          <h1 style={{ fontFamily: "'GeistPixel',monospace", fontSize: 24, color: C.dark, marginBottom: 8 }}>Check Your Inbox</h1>
          <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>We sent a confirmation link to:</p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(21,98,200,0.08)', border: '1px solid rgba(21,98,200,0.2)', borderRadius: 20, padding: '6px 14px', fontSize: 13, color: C.blue, fontWeight: 600, marginBottom: 20, wordBreak: 'break-all' }}>
            ✉️ {email}
          </div>
          <p style={{ color: C.brown, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
            Click the link in that email to verify your address, then come back and sign in.
          </p>
          <button
            onClick={handleResend}
            disabled={loading}
            style={{ ...primaryBtnStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.navy}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
          >
            <RefreshCw size={16} /> {loading ? 'Sending...' : 'Resend verification email'}
          </button>
          <button
            onClick={() => { setAuthView('signup'); setSignupStep(1); setError(''); }}
            style={{ width: '100%', background: 'none', border: 'none', color: C.brown, fontSize: 13, cursor: 'pointer', fontFamily: "'Roboto',sans-serif", padding: '8px' }}
          >
            Wrong email? Go back
          </button>
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid rgba(42,33,24,0.2)` }}>
            <span style={{ color: C.brown, fontSize: 13 }}>Already verified? </span>
            <button
              onClick={() => switchView('login')}
              style={{ background: 'none', border: 'none', color: C.blue, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Roboto',sans-serif" }}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── SIGNUP STEP 2 — Profile info ──────────────────────────────────────────
  if (authView === 'signup' && signupStep === 2) {
    return (
      <div style={{ ...pageStyle, overflowY: 'auto' }}>
        <Scanlines />
        <div style={cardStyle(true)}>
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontFamily: "'GeistPixel',monospace", fontSize: 22, color: C.dark, marginBottom: 4 }}>Complete Your Profile</h1>
            <p style={{ color: C.brown, fontSize: 13, lineHeight: 1.5 }}>
              {role === 'composer' ? '🎵 Composer' : role === 'organization' ? '🏢 Sync Agency' : '🎯 Music Executive'} · This appears on your public profile.
            </p>
          </div>

          <StepBar step={2} />

          <form onSubmit={handleAuth}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#a0896e', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(42,33,24,0.12)' }}>
              Basic Info
            </div>

            <div style={{ display: "flex", flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>First Name *</label>
                <input type="text" placeholder="Jane" value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Last Name *</label>
                <input type="text" placeholder="Doe" value={lastName} onChange={e => setLastName(e.target.value)} required style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Location <span style={{ color: C.brown, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <input type="text" placeholder="New York, NY" value={location} onChange={e => setLocation(e.target.value)} style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Bio <span style={{ color: C.brown, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <textarea
                placeholder="Tell us about yourself..."
                value={bio}
                onChange={e => setBio(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'none' }}
                onFocus={focusInput}
                onBlur={blurInput}
              />
            </div>

            {role === 'composer' && (
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>
                  Genres <span style={{ color: C.brown, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {GENRE_LIST.map(g => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g])}
                      style={{
                        background: genres.includes(g) ? C.blue : C.cream,
                        color: genres.includes(g) ? C.white : C.brown,
                        border: genres.includes(g) ? `1px solid ${C.blue}` : BORDER,
                        borderRadius: 20, padding: "5px 13px", fontSize: 12, fontWeight: 600,
                        cursor: "pointer", fontFamily: "'Roboto',sans-serif", transition: 'all 0.12s',
                      }}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ margin: '18px 0 6px', padding: '14px 16px', background: 'rgba(240,191,107,0.08)', border: '1px solid rgba(240,191,107,0.3)', borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={e => setMarketingConsent(e.target.checked)}
                  style={{ marginTop: 2, width: 15, height: 15, flexShrink: 0, accentColor: C.blue, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, color: C.brown, lineHeight: 1.6, fontFamily: "'Roboto',sans-serif" }}>
                  I'd like to receive product updates, new features, and weekly sync tips from Coda-Vault.
                  <span style={{ color: 'rgba(85,56,31,0.55)', display: 'block', marginTop: 2, fontSize: 12 }}>
                    You can unsubscribe at any time. We never share your data.
                  </span>
                </span>
              </label>
            </div>

            <ErrorMsg msg={error} />

            <button
              type="submit"
              disabled={loading}
              style={primaryBtnStyle}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.navy}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
            >
              {loading ? "Creating Account..." : "Create Account →"}
            </button>

            <button
              type="button"
              onClick={() => { setSignupStep(1); setError(""); }}
              style={{ width: '100%', marginTop: 4, background: 'none', border: 'none', color: C.brown, fontSize: 13, cursor: 'pointer', fontFamily: "'Roboto',sans-serif", padding: '8px' }}
            >
              ← Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── LOGIN / SIGNUP STEP 1 / FORGOT PASSWORD ───────────────────────────────
  const headings = {
    login:  { title: 'WELCOME BACK',   subtitle: 'Sign in to Coda-Vault' },
    signup: { title: 'Create Account', subtitle: 'Join the sync licensing platform built for independent music.' },
    forgot: { title: 'Reset Password', subtitle: "Enter your email and we'll send a reset link" },
  };

  return (
    <div style={pageStyle}>
      <Scanlines />
      <div style={cardStyle()}>

        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/cv-logo.svg" alt="Coda-Vault" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 10, margin: '0 auto 14px', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 style={{ fontFamily: "'GeistPixel',monospace", fontSize: 24, color: C.dark, marginBottom: 6 }}>
            {headings[authView].title}
          </h1>
          <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.5 }}>
            {headings[authView].subtitle}
          </p>
        </div>

        {authView === 'signup' && <StepBar step={1} />}

        <form onSubmit={authView === 'signup' ? handleNextStep : handleAuth}>

          {/* Google button — first option for speed */}
          {onGoogleSignIn && authView !== 'forgot' && (
            <>
              <button
                type="button"
                onClick={onGoogleSignIn}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: C.white, color: C.dark, border: BORDER, borderRadius: 8, padding: '11px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: "'Roboto',sans-serif", transition: 'box-shadow 0.1s, transform 0.1s', marginBottom: 16 }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = `2px 2px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(-1px,-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translate(0,0)'; }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Continue with Google
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(42,33,24,0.2)' }} />
                <span style={{ color: C.brown, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(42,33,24,0.2)' }} />
              </div>
            </>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              aria-label="Email address"
              style={inputStyle}
              onFocus={focusInput}
              onBlur={blurInput}
            />
          </div>

          {authView !== 'forgot' && (
            <div style={{ marginBottom: authView === 'login' ? 8 : 20 }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={authView === 'signup' ? '8+ characters' : '••••••••'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  aria-label="Password"
                  style={{ ...inputStyle, paddingRight: 44 }}
                  onFocus={focusInput}
                  onBlur={blurInput}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: C.brown }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {authView === 'signup' && password.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= passwordStrength.level ? passwordStrength.color : 'rgba(42,33,24,0.15)', transition: 'background 0.3s' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: passwordStrength.color, fontFamily: "'Roboto',sans-serif" }}>{passwordStrength.label}</span>
                </div>
              )}
            </div>
          )}

          {/* Confirm password — signup only */}
          {authView === 'signup' && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  aria-label="Confirm password"
                  style={{
                    ...inputStyle,
                    paddingRight: 44,
                    borderColor: confirmPassword && confirmPassword !== password ? C.red : C.dark,
                  }}
                  onFocus={focusInput}
                  onBlur={blurInput}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(p => !p)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: C.brown }}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <span style={{ fontSize: 12, color: C.red, marginTop: 4, display: 'block' }}>Passwords don't match</span>
              )}
            </div>
          )}

          {/* Role picker — signup only */}
          {authView === 'signup' && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>I am a:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { type: 'composer',  label: 'Composer',       desc: 'I create music for sync',       icon: '🎵' },
                  { type: 'executive', label: 'Music Executive', desc: 'I search and license music',    icon: '🎯' },
                ].map(({ type, label, desc, icon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setRole(type)}
                    style={{
                      padding: '16px 12px', borderRadius: 12, textAlign: 'center',
                      border: role === type ? `2px solid ${C.blue}` : BORDER,
                      background: role === type ? C.white : C.cream,
                      cursor: 'pointer', fontFamily: "'Roboto',sans-serif",
                      outline: role === type ? `2px solid ${C.blue}` : 'none',
                      boxShadow: role === type ? `2px 2px 0 ${C.blue}` : 'none',
                      transition: 'all 0.1s',
                    }}
                    onMouseEnter={e => { if (role !== type) { e.currentTarget.style.boxShadow = `2px 2px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(-1px,-1px)'; }}}
                    onMouseLeave={e => { if (role !== type) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translate(0,0)'; }}}
                  >
                    <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 2, color: C.dark }}>{label}</div>
                    <div style={{ fontSize: 12, color: C.brown }}>{desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {authView === 'login' && (
            <div style={{ textAlign: 'right', marginBottom: 16, marginTop: -4 }}>
              <button
                type="button"
                onClick={() => switchView('forgot')}
                style={{ background: 'none', border: 'none', color: C.brown, fontSize: 12, cursor: 'pointer', fontFamily: "'Roboto',sans-serif" }}
              >
                forgot password?
              </button>
            </div>
          )}

          {authView === 'forgot' && <div style={{ height: 12 }} />}

          {/* Terms agreement — signup step 1 only */}
          {authView === 'signup' && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, accentColor: C.blue, flexShrink: 0, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: C.brown, lineHeight: 1.5 }}>
                I have read and agree to the{' '}
                <a href="#terms" target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontWeight: 600 }}>Terms of Service</a>
                {' '}and{' '}
                <a href="#privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontWeight: 600 }}>Privacy Policy</a>
              </span>
            </label>
          )}

          <ErrorMsg msg={error} />

          <button
            type="submit"
            disabled={loading}
            style={primaryBtnStyle}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.navy}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
          >
            {loading ? "Loading..." : authView === 'signup' ? "Create Account →" : authView === 'forgot' ? "Send Reset Link" : "Sign In"}
          </button>

        </form>

        <div style={{ textAlign: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid rgba(42,33,24,0.2)` }}>
          {authView === 'forgot' ? (
            <button
              onClick={() => switchView('login')}
              style={{ background: "none", border: "none", color: C.blue, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Roboto',sans-serif" }}
            >
              ← Back to Sign In
            </button>
          ) : (
            <>
              <span style={{ color: C.brown, fontSize: 14 }}>
                {authView === 'signup' ? 'Already have an account?' : "Don't have an account?"}
              </span>
              {' '}
              <button
                onClick={() => switchView(authView === 'signup' ? 'login' : 'signup')}
                style={{ background: "none", border: "none", color: C.blue, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Roboto',sans-serif" }}
              >
                {authView === 'signup' ? 'Sign In' : 'Create Account →'}
              </button>
            </>
          )}
          {onBackToLanding && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={onBackToLanding}
                style={{ background: "none", border: "none", color: C.brown, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Roboto',sans-serif" }}
              >
                ← Back to Landing
              </button>
            </div>
          )}
          {onApplyAgency && authView === 'login' && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid rgba(42,33,24,0.15)`, textAlign: 'center' }}>
              <span style={{ color: C.brown, fontSize: 13 }}>Sync Agency? </span>
              <button
                type="button"
                onClick={onApplyAgency}
                style={{ background: "none", border: "none", color: C.blue, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Roboto',sans-serif" }}
              >
                Apply for an account →
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
